import { useFormContext } from "react-hook-form";

/**
 * CLE-2971 — the page's one "an eligibility check is pending" signal.
 *
 * A form field, like `isEditingMessage`, so `useOrderReadiness` reads it from the react-hook-form
 * context every order page (new, copy, review) already provides — no extra provider to forget on one
 * of them. It is a COUNTER, not a flag, because holders overlap: each side's Run Eligibility button for
 * the whole of a run, and each side's page trigger from the moment the CPT codes change until the
 * re-run it asked for settles. `mapFormToRequestBody` maps fields by name, so it is never sent.
 *
 * Each `holdEligibilityActivity` returns its own release, which is idempotent, and the count never goes
 * below zero — so a hold released twice (its `finally` and an unmount) cannot cancel another's hold.
 */
export const PENDING_ELIGIBILITY_RUNS_FIELD = "pendingEligibilityRuns";

interface IEligibilityActivityForm {
  getValues: (name: any) => any;
  setValue: (name: any, value: any) => void;
}

const readPending = (form: IEligibilityActivityForm): number => {
  const value = Number(form.getValues(PENDING_ELIGIBILITY_RUNS_FIELD));
  return Number.isFinite(value) && value > 0 ? value : 0;
};

export function holdEligibilityActivity(
  form: IEligibilityActivityForm
): () => void {
  form.setValue(PENDING_ELIGIBILITY_RUNS_FIELD, readPending(form) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    form.setValue(
      PENDING_ELIGIBILITY_RUNS_FIELD,
      Math.max(0, readPending(form) - 1)
    );
  };
}

/** True while any eligibility check on the page is debouncing, being evaluated or in flight. */
export function useEligibilityActivity(): boolean {
  const { watch } = useFormContext();
  return Number(watch(PENDING_ELIGIBILITY_RUNS_FIELD)) > 0;
}
